import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../../Components/ui/dataTable";
import AssignRider from "./AssignRider";
import { Button } from "../../Components/ui/button";
import { Modal, ModalContent, ModalTrigger } from "../../Components/ui/modal";
import TrackOrder from "./TrackOrder";
import OrderProgressTrack from "./OrderProgressTrack";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../Components/ui/select";
import { DatePickerWithRange } from "../../Components/ui/dateRange";
import { useDispatch, useSelector } from "react-redux";
import { useGetAllOrdersQuery } from "../Orders/OrderApi";
import { SkeletonTable } from "../../Components/ui/skeleton";
import Pagination from "../../Components/ui/pagination";
import { format, subDays } from "date-fns";
import MapComponentBeta from "../../Components/MapComponentBeta";
import { updateCurrentOrders } from "../../Lib/slices/locationSlice";
import clsx from "clsx";
import { ProtectedRoutes } from "../../config/protectedRoutes";

const PAYMENT_VARIANT_MAP = {
  paid: "successful",
  unpaid: "pending",
};

const DATE_MODES = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "custom", label: "Custom" },
];

const Dashboard = () => {
  const { roleId } = useSelector((state) => state.auth.data);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [dateMode, setDateMode] = useState("today");
  const today = new Date();
  const [customRange, setCustomRange] = useState({ from: today, to: today });

  let startDate;
  let endDate;
  if (dateMode === "today") {
    startDate = endDate = format(today, "yyyy-MM-dd");
  } else if (dateMode === "yesterday") {
    startDate = endDate = format(subDays(today, 1), "yyyy-MM-dd");
  } else {
    startDate = format(customRange?.from || today, "yyyy-MM-dd");
    endDate = format(customRange?.to || today, "yyyy-MM-dd");
  }

  const { data, isLoading: loading } = useGetAllOrdersQuery(
    { page, status, startDate, endDate },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );

  useEffect(() => {
    if (data?.data) {
      dispatch(updateCurrentOrders(data.data));
    }
  }, [data, dispatch]);

  // Memoized + given a stable getRowId below so polling every 5s doesn't
  // churn row identity mid-render and reset an open Track Order modal.
  const columns = useMemo(
    () => [
      {
        accessorKey: "customerName",
        header: "Customer Name",
        cell: ({ row }) => (
          <div className='capitalize'>{row.getValue("customerName")}</div>
        ),
      },
      {
        accessorKey: "item",
        header: "Item",
        cell: ({ row }) => (
          <div className='capitalize'>{row.getValue("item")}</div>
        ),
      },
      {
        accessorKey: "rider",
        header: "Rider",
        cell: ({ row }) => (
          <div className='capitalize'>{row.getValue("rider") || "Unassigned"}</div>
        ),
      },
      {
        id: "progress",
        header: "Progress",
        cell: ({ row }) => (
          <OrderProgressTrack
            statuses={row.original.statusHistory}
            currentStatus={row.original.status}
          />
        ),
      },
      {
        accessorKey: "paymentStatus",
        header: "Payment Status",
        cell: ({ row }) => {
          const s = row.getValue("paymentStatus");
          const variant = PAYMENT_VARIANT_MAP[s] || "pending";
          return (
            <Button asChild variant={variant} size='sm'>
              <p className='capitalize'>{s || "N/A"}</p>
            </Button>
          );
        },
      },
      {
        id: "track",
        cell: ({ row }) => {
          const orderId = row.original.id;
          return (
            <Modal>
              <ModalContent name='Track Order' className='w-[92vw] max-w-[860px]'>
                <TrackOrder id={orderId} />
              </ModalContent>
              <ModalTrigger>
                <Button
                  variant='outline'
                  className='outline-none focus:outline-none focus-visible:outline-none px-4'
                >
                  Track
                </Button>
              </ModalTrigger>
            </Modal>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className='mb-20 bg-white'>
      {roleId !== 4 && (
        <div className='flex w-full mb-5 flex-col md:grid gap-4 md:grid-cols-2 lg:gap-6'>
          <AssignRider />
          <MapComponentBeta />
        </div>
      )}
      <div className='bg-white p-2'>
        <div className='flex flex-col md:flex-row items-center justify-between my-2 gap-3'>
          <p className='font-bold text-primaryBlack text-md'>
            Current Orders - {data?.total ?? 0}
          </p>

          <div className='flex flex-wrap items-center gap-3'>
            <Select
              value={status === "" ? "all" : status}
              onValueChange={(val) => {
                setStatus(val === "all" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className='w-[150px] text-sm'>
                <SelectValue placeholder='All Status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Status</SelectItem>
                <SelectItem value='pending'>Pending</SelectItem>
                <SelectItem value='picked_up'>Picked Up</SelectItem>
                <SelectItem value='in_progress'>In Progress</SelectItem>
                <SelectItem value='delivered'>Delivered</SelectItem>
                <SelectItem value='cancelled'>Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <div className='flex items-center gap-1 font-semibold bg-[#F5FEF1] w-auto p-1 rounded-sm'>
              {DATE_MODES.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => {
                    setDateMode(mode.value);
                    setPage(1);
                  }}
                  className={clsx(
                    "text-primaryBlack rounded-sm text-sm p-2 hover:bg-primaryGreen/20",
                    dateMode === mode.value && "bg-primaryGreen text-white",
                  )}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            {dateMode === "custom" && (
              <DatePickerWithRange
                date={customRange}
                setDate={(range) => {
                  setCustomRange(range);
                  setPage(1);
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div className='table-scroll max-h-[70vh] overflow-auto  max-w-full rounded-md border'>
        <div className='w-full'>
          {loading ? (
            <SkeletonTable />
          ) : (
            <DataTable
              columns={columns}
              data={data?.data ?? []}
              getRowId={(row) => row.id}
            />
          )}
        </div>
      </div>
      {!loading && (
        <Pagination
          page={page}
          limit={data?.limit ?? 0}
          total={data?.total ?? 0}
          setPage={setPage}
        />
      )}
    </div>
  );
};

export default ProtectedRoutes(Dashboard, [5, 4, 2, 1]);
